import { useRef, useCallback } from "react";
import MapGL, { Source, Layer, Popup, MapRef, MapLayerMouseEvent } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { Box, Typography, Alert } from "@mui/material";
import { DEFAULT_TILE_SOURCE } from "../../../../../helpers/JobMap/tileSources";
import { GeoPreviewMapProps, WARNING_THRESHOLD } from "./GeoPreviewMap.types";
import { useGeoPreviewMapViewModel } from "./GeoPreviewMap.vm";
import {
	previewPolygonFillLayer,
	previewPolygonLineLayer,
	previewPointCircleLayer,
	POLYGON_FILL_LAYER_ID,
	POINT_CIRCLE_LAYER_ID,
} from "./GeoPreviewMap.layers";
import "./GeoPreviewMap.css";

export function GeoPreviewMap(props: GeoPreviewMapProps) {
	const vm = useGeoPreviewMapViewModel(props);
	const mapRef = useRef<MapRef>(null);

	const fitBounds = useCallback(() => {
		const map = mapRef.current;
		if (!map || !vm.processedFeatures.bounds) return;
		const [swLng, swLat, neLng, neLat] = vm.processedFeatures.bounds;
		map.fitBounds([swLng, swLat, neLng, neLat], { padding: 50 });
	}, [vm.processedFeatures.bounds]);

	const handleClick = useCallback(
		(e: MapLayerMouseEvent) => {
			const feature = e.features?.[0];
			if (!feature) {
				vm.handleMapClick();
				return;
			}
			const featureKey = feature.properties?.featureKey as string | undefined;
			if (!featureKey) return;
			const originalProps = vm.processedFeatures.propertiesIndex.get(featureKey);
			if (originalProps) {
				vm.onFeatureClick({ lng: e.lngLat.lng, lat: e.lngLat.lat }, originalProps);
			}
		},
		[vm.handleMapClick, vm.onFeatureClick, vm.processedFeatures.propertiesIndex],
	);

	// Empty state
	if (vm.processedFeatures.totalFeatures === 0) {
		return (
			<Box className="geo-preview-map-empty">
				<Typography variant="body2" color="text.secondary">
					No geodata available to display
				</Typography>
			</Box>
		);
	}

	return (
		<Box className="geo-preview-map">
			{vm.processedFeatures.totalFeatures > WARNING_THRESHOLD && (
				<Alert severity="warning">
					Displaying {vm.processedFeatures.totalFeatures} features. Map may be slow to interact with.
				</Alert>
			)}
			<Box className="map-container">
				<MapGL
					ref={mapRef}
					initialViewState={{ latitude: 40, longitude: -95, zoom: 4 }}
					style={{ width: "100%", height: "100%" }}
					mapStyle={DEFAULT_TILE_SOURCE.style}
					interactiveLayerIds={[POLYGON_FILL_LAYER_ID, POINT_CIRCLE_LAYER_ID]}
					onLoad={fitBounds}
					onClick={handleClick}
				>
					<Source id="preview-polygons" type="geojson" data={vm.processedFeatures.polygonFC}>
						<Layer {...previewPolygonFillLayer} />
						<Layer {...previewPolygonLineLayer} />
					</Source>
					<Source id="preview-points" type="geojson" data={vm.processedFeatures.pointFC}>
						<Layer {...previewPointCircleLayer} />
					</Source>
					{vm.activePopup && (
						<Popup
							longitude={vm.activePopup.lngLat.lng}
							latitude={vm.activePopup.lngLat.lat}
							onClose={vm.handleMapClick}
							closeOnClick={false}
							maxWidth="250px"
						>
							<Box className="popup-content">
								{Object.keys(vm.activePopup.properties).length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										No properties
									</Typography>
								) : (
									Object.entries(vm.activePopup.properties).map(([key, value]) => (
										<Typography key={key} variant="body2">
											<strong>{key}:</strong> {String(value ?? "")}
										</Typography>
									))
								)}
							</Box>
						</Popup>
					)}
				</MapGL>
			</Box>
		</Box>
	);
}
